import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import { useIsBreakpoint } from "../../../../utils/V2/screen"
import Link from "../../Link"
import ArrowIcon from "../Icons/ArrowIcon"
import Typography, { TypographySize } from "../Typography"

import {
  SharedButtonClickProps,
  handleOnClick,
} from "@utils/buttonEventHandler"
import {
  getBorderColorClass,
  getBackgroundColorClass,
  getBackgroundHoverColorClass,
  getBackgroundGroupHoverColorClass,
  getAccentColor,
  getTextColorClass,
  getTextHoverColorClass,
} from "@utils/V2/color"
import React, { ReactNode, useMemo } from "react"

type Props = {
  style: "solid" | "bordered"
  size: "large" | "medium" | "small"
  type?: "button" | "submit"
  color: Color
  overrideTextColor?: Color
  text: string
  fontSize?: TypographySize
  mobileText?: string
  linkUrl?: string | YocoCom.YocoLink
  queryParams?: string
  mobileLinkUrl?: string | YocoCom.YocoLink
  onClick?: () => void
  transparentHover?: boolean
  hideIcon?: boolean
  icon?: ReactNode
  iconPosition?: "left" | "right"
  disabled?: boolean
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  className?: string
  secondaryText?: string
  hasScrollArrow?: boolean
  "data-testid"?: string
  decorative?: boolean
  ariaPressed?: boolean
}

const PillButton = ({
  style,
  size = "large",
  type = "button",
  color,
  overrideTextColor = getAccentColor(color),
  text,
  mobileText,
  fontSize,
  linkUrl,
  queryParams,
  mobileLinkUrl,
  onClick,
  transparentHover = false,
  hideIcon = false,
  icon = <ArrowIcon />,
  iconPosition = "right",
  disabled = false,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  className,
  secondaryText,
  hasScrollArrow = false,
  "data-testid": dataTestId,
  decorative = false,
  ariaPressed,
}: Props) => {
  const isMobile = useIsBreakpoint("mobile")
  const isTablet = useIsBreakpoint("tablet")
  const sizeOverride = isMobile || isTablet ? "medium" : size

  const sharedButtonClickProps: SharedButtonClickProps = {
    trackingEvent,
    trackingEventKey,
    trackingEventValue,
    onClick,
  }
  const desktopLinkUrl = typeof linkUrl === "string" ? linkUrl : linkUrl?.url
  const mobileResolvedLinkUrl =
    typeof mobileLinkUrl === "string" ? mobileLinkUrl : mobileLinkUrl?.url

  const buttonClasses = useMemo(
    () =>
      classNames(
        getBorderColorClass(color),
        style === "solid"
          ? getBackgroundColorClass(color)
          : [
              "bg-transparent transition-colors duration-300",
              getBackgroundHoverColorClass(color),
              transparentHover && "hover:bg-opacity-10",
            ],
        { "opacity-50 pointer-events-none": disabled },
        "group rounded-32 border flex gap-10 items-center w-full sm:w-max justify-center",
        {
          "px-20 py-[15px] md:px-[18px] md:py-[13px] lg:px-20 lg:py-[15px]": [
            "large",
            "medium",
          ].includes(sizeOverride),
        },
        {
          "px-16 py-[9px]": size === "small",
        },
        { "flex-row-reverse": iconPosition === "left" },
        className
      ),
    [
      color,
      style,
      transparentHover,
      disabled,
      sizeOverride,
      size,
      iconPosition,
      className,
    ]
  )

  const buttonContentClasses = classNames(
    "!leading-none transition-colors duration-300",
    style === "bordered" &&
      !transparentHover &&
      getTextHoverColorClass(getAccentColor(color))
  )

  const buttonContent = (
    <>
      <div className="flex gap-10 pt-[2px]">
        {mobileText && (
          <Typography
            text={mobileText}
            weight="medium"
            font="grotesk"
            color={overrideTextColor}
            size={
              fontSize || (size === "large" ? "body-lg-dual" : "body-md-fixed")
            }
            className={classNames(buttonContentClasses, { "sm:hidden": text })}
          />
        )}
        {text && (
          <Typography
            text={text}
            weight="medium"
            font="grotesk"
            color={overrideTextColor}
            size={
              fontSize || (size === "large" ? "body-lg-dual" : "body-md-fixed")
            }
            className={classNames(buttonContentClasses, {
              "hidden sm:block": mobileText,
            })}
          />
        )}

        {secondaryText && (
          <Typography
            text={secondaryText}
            weight="medium"
            font="grotesk"
            color={overrideTextColor}
            size={
              fontSize || (size === "large" ? "body-lg-dual" : "body-md-fixed")
            }
            className={buttonContentClasses}
          />
        )}
      </div>
      {!hideIcon && (
        <div
          className={classNames(
            getTextColorClass(overrideTextColor),
            { "rotate-90 pl-[2px]": hasScrollArrow },
            hasScrollArrow && size === "small"
              ? "w-16 !pl-0"
              : "w-20 md:w-[22px] lg:w-24",
            buttonContentClasses
          )}
        >
          {icon}
        </div>
      )}
    </>
  )

  if (decorative) {
    return (
      <span
        className={classNames(
          buttonClasses,
          style === "bordered" && getBackgroundGroupHoverColorClass(color)
        )}
      >
        {buttonContent}
      </span>
    )
  }

  if (linkUrl || mobileLinkUrl) {
    return (
      <>
        {mobileLinkUrl && (
          <Link
            link={mobileLinkUrl}
            onClick={(e) =>
              handleOnClick({
                e,
                linkUrl: mobileResolvedLinkUrl,
                ...sharedButtonClickProps,
              })
            }
            className={classNames(buttonClasses, { "sm:hidden": linkUrl })}
            queryParams={queryParams}
          >
            {buttonContent}
          </Link>
        )}
        {linkUrl && (
          <Link
            link={linkUrl}
            onClick={(e) =>
              handleOnClick({
                e,
                linkUrl: desktopLinkUrl,
                ...sharedButtonClickProps,
              })
            }
            className={classNames(buttonClasses, {
              "hidden sm:flex": mobileLinkUrl,
            })}
            queryParams={queryParams}
          >
            {buttonContent}
          </Link>
        )}
      </>
    )
  }

  return (
    <button
      type={type}
      onClick={(e) => handleOnClick({ e, ...sharedButtonClickProps })}
      className={buttonClasses}
      disabled={disabled}
      data-testid={dataTestId}
      aria-pressed={ariaPressed}
    >
      {buttonContent}
    </button>
  )
}

export default PillButton
